import { useCan } from '@refinedev/core';
import type { ColumnDef } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ErrorNotice } from '../components/ErrorNotice';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  DataTable,
  DataTableColumnHeader,
  EmptyState,
  Loading,
  Notice,
  Toasts,
  useToasts,
} from '../components/ui';
import { closeSession, listActiveSessions, type ActiveSession } from '../data/sessions';
import { useHierarchy } from '../hierarchy/HierarchyProvider';
import { timeAgo } from '../lib/format';

/**
 * Sesiones de soporte activas (Módulo 8).
 *
 * Quién está en qué nodo, ahora. Sirve para dos cosas distintas: no pisarse dos
 * técnicos en la misma máquina, y ver una sesión que nadie recuerda haber abierto
 * —que es lo que hay que mirar cuando algo huele raro—.
 *
 * Se refresca por reloj y no por el canal en vivo porque el WebSocket del panel
 * proyecta eventos **de nodo** (estado, salud, inventario), no de sesión: no hay
 * `session.opened` que escuchar. Cuando el backend lo publique, este intervalo se
 * cambia por una suscripción y la tabla no se toca.
 *
 * **Cerrar una sesión ajena no tiene ruta todavía**: existe
 * `POST /internal/terminal/sessions/:id/close`, pero va con el token interno del
 * gateway y dárselo al panel sería regalarle una credencial de servicio.
 */

const REFRESH_MS = 10_000;

const KIND_LABEL: Record<ActiveSession['kind'], string> = {
  terminal: 'Terminal',
  screen: 'Pantalla',
};

export function SessionsPage() {
  const { nodes } = useHierarchy();
  const { data: canClose } = useCan({ resource: 'sessions', action: 'delete' });

  const [sessions, setSessions] = useState<ActiveSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [closing, setClosing] = useState<ActiveSession | null>(null);
  const [closeError, setCloseError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const toasts = useToasts();

  const load = useCallback(async () => {
    try {
      setSessions(await listActiveSessions());
      setError(null);
    } catch (cause) {
      setError(cause);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const timer = setInterval(() => void load(), REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  const confirmClose = async () => {
    if (!closing) return;
    setBusy(true);
    setCloseError(null);
    try {
      await closeSession(closing.id);
      toasts.ok('Sesión cerrada.');
      setClosing(null);
      await load();
    } catch (cause) {
      setClosing(null);
      setCloseError(cause);
    } finally {
      setBusy(false);
    }
  };

  const aliasOf = (nodeId: string): string =>
    nodes.find((node) => node.id === nodeId)?.alias ?? `${nodeId.slice(0, 8)}…`;

  const locationOf = (nodeId: string): string | null => {
    const node = nodes.find((candidate) => candidate.id === nodeId);
    return node ? `${node.clientName} · ${node.branchName}` : null;
  };

  const columns = useMemo<ColumnDef<ActiveSession>[]>(
    () => [
      {
        id: 'tipo',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Tipo" />,
        accessorFn: (session) => KIND_LABEL[session.kind],
        meta: { label: 'Tipo' },
        cell: ({ row }) => (
          <Badge tone={row.original.kind === 'screen' ? 'warn' : 'neutral'}>
            {KIND_LABEL[row.original.kind]}
          </Badge>
        ),
      },
      {
        id: 'nodo',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Nodo" />,
        accessorFn: (session) => aliasOf(session.nodeId),
        meta: { label: 'Nodo' },
        cell: ({ row }) => (
          <>
            <Link to={`/nodes/${row.original.nodeId}`} className="hover:underline">
              {aliasOf(row.original.nodeId)}
            </Link>
            {locationOf(row.original.nodeId) && (
              <div className="text-[11px] opacity-50">{locationOf(row.original.nodeId)}</div>
            )}
          </>
        ),
      },
      {
        id: 'operador',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Operador" />,
        accessorFn: (session) => session.operator,
        meta: { label: 'Operador' },
        cell: ({ getValue }) => <span className="opacity-80">{getValue<string>()}</span>,
      },
      {
        id: 'abierta',
        header: ({ column }) => <DataTableColumnHeader column={column} title="Abierta" />,
        accessorFn: (session) => session.startedAt.getTime(),
        meta: { label: 'Abierta' },
        cell: ({ row }) => (
          <span className="opacity-70">
            {timeAgo(row.original.startedAt)}
            <div className="text-[11px] opacity-60">
              {row.original.startedAt.toLocaleTimeString('es')}
            </div>
          </span>
        ),
      },
      {
        id: 'acciones',
        header: 'Acciones',
        meta: { label: 'Acciones' },
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex justify-end">
            <Button
              variant="danger"
              disabled={!canClose?.can}
              title={canClose?.can ? 'Cerrar la sesión' : canClose?.reason}
              onClick={() => setClosing(row.original)}
            >
              Cerrar
            </Button>
          </div>
        ),
      },
    ],
    // `aliasOf`/`locationOf` no entran: son funciones nuevas en cada render que
    // ya dependen de `nodes` (sí listado), así que añadirlas anularía la
    // memoización sin cambiar qué se lee.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [nodes, canClose],
  );

  return (
    <section className="grid gap-4">
      <Toasts toasts={toasts.toasts} />

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Sesiones activas</h1>
          <p className="text-sm opacity-60">
            Quién está trabajando en qué nodo. Se actualiza cada {REFRESH_MS / 1_000} s.
          </p>
        </div>
        <Button variant="ghost" onClick={() => void load()}>
          Recargar
        </Button>
      </div>

      {error !== null && <ErrorNotice error={error} what="Listar las sesiones" />}

      {closeError !== null && (
        <ErrorNotice
          error={closeError}
          what="Cerrar la sesión"
          pendingHint="Falta `POST /sessions/:id/close` con el JWT del operador; hoy solo existe la variante interna del gateway."
        />
      )}

      <Card>
        {loading && sessions.length === 0 ? (
          <Loading label="Consultando sesiones…" />
        ) : sessions.length === 0 ? (
          <EmptyState title="Ninguna sesión abierta">
            Nadie tiene una terminal ni una pantalla remota abierta dentro de tu alcance.
          </EmptyState>
        ) : (
          <DataTable
            columns={columns}
            data={sessions}
            pageSize={0}
            emptyMessage="Ninguna sesión abierta."
          />
        )}
      </Card>

      <ConfirmDialog
        open={closing !== null}
        title="Cerrar una sesión de otro operador"
        confirmLabel="Cerrar la sesión"
        busy={busy}
        onCancel={() => setClosing(null)}
        onConfirm={() => void confirmClose()}
      >
        <p>
          {closing && KIND_LABEL[closing.kind]} de <strong>{closing?.operator}</strong> en{' '}
          <strong>{closing && aliasOf(closing.nodeId)}</strong>.
        </p>
        <Notice tone="warn">
          Se corta en el momento, sin avisar a quien la tiene abierta: si está a mitad de una
          intervención, la deja a medias. Queda en la bitácora a tu nombre.
        </Notice>
      </ConfirmDialog>
    </section>
  );
}
